<script setup>
  import { ref, onMounted, getCurrentInstance, inject, defineEmits, defineProps } from 'vue'
  import { useRouter } from 'vue-router'
  import { usePageLoadingStore } from "../../../../../../stores/pageLoading";
  import JobFairJobNoticeDetailModal from "@/pages/user/components/modal/jobFair/JobFairJobNoticeDetailModal.vue";
  import mypageJobFairApplicantService from '../../../../services/mypage/jobFair/mypageJobFairApplicantService';
  import JobFairResumeDetailModal from '../../../../components/modal/jobFair/JobFairResumeDetailModal.vue';
  import mypageJobFairService from "../../../../services/mypage/jobFair/mypageJobFairService";
  import moment from 'moment';

  const globals = getCurrentInstance().appContext.config.globalProperties;
  const { $yyyymmddHHmmss, $toast, $gbNameKR, $userGbKR, $number, $pageListBoardNo, alert, confirm } = globals;

  const router = useRouter()
  const loadingStore = usePageLoadingStore()

  const currentPage = ref(1);
  const pageSize = ref(10);
  const totalPages = ref(1);
  const totalCount = ref(0);
  const list = ref([]);

  const props = defineProps({
    jobNoticeSeq: {
      type: String,
      default: '',
    },
    fileYn: {
      type: String,
      default: '',
    },
    corpId: {
      type: String,
      default: '',
    },
    userId: {
      type: String,
      default: '',
    },
  })

  const pageSizeGroup = ref([
    { label:"10개씩 보기", value: "10" },
    { label:"30개씩 보기", value: "30" },
    { label:"50개씩 보기", value: "50" },
    { label:"100개씩 보기", value: "100" },
  ])

  // 모달
  const modalContent = ref([]);
  const okButtonText = ref("확인");
  const cancelButtonText = ref("취소");
  const jobNoticeModal = ref(null); // 채용공고
  const jobFairResumeModal = ref(null); // 이력서

  const retrieveList = async () => {
    const paramObj = {
      jobNoticeSeq: props.jobNoticeSeq,
      pageNumber: currentPage.value,
      size: pageSize.value,
    };

    loadingStore.loading = true;
    mypageJobFairApplicantService.retrieveApplicantListPagingByJobNoticeSeq(paramObj)
      .then( async (res) => {
        loadingStore.loading = false;
        if(res.data.code.toLowerCase() === 'success') {
          const result = res.data.result;
          totalPages.value = result.totalPages;
          totalCount.value = result.totalElements;
          list.value = $pageListBoardNo(result.content, totalCount.value, pageSize.value, currentPage.value);
        }
        loadingStore.loading = false;
      })
      .catch( (res) =>  {
        loadingStore.loading = false;
      });
  }

  const onSelectedPage = (pageNo) => {
      currentPage.value = pageNo;
      retrieveList();
  }

  // 페이지당 개수 변경
  const onSelectPageSize = (option) => {
    pageSize.value = option.value;
    currentPage.value = 1;
    retrieveList();
  }

  const onClickJobNotice = async () => {
    await jobNoticeModal.value.show({
      jobNoticeSeq: props.jobNoticeSeq,
      corpId: props.corpId,
    });
  };

  const onClickDownload = async () => {
    if(totalCount.value > 0) {
      modalContent.value = ["입사지원 목록을 내 PC로 다운로드할까요?"];
      okButtonText.value = "다운로드";
      const ok = await confirm(modalContent.value, okButtonText.value);
      if(ok){
        _startDownload();
      }
    } else {
      alert(["다운로드 받을 '입사지원 목록'이 존재하지 않습니다."]);
    }
  }

  const _startDownload = () => {
    $toast('', `다운로드 데이터 생성을 시작합니다.`);
    loadingStore.loading = true;

    let paramObj = {
      userId: props.userId,
      jobNoticeSeq: props.jobNoticeSeq,
    }

    mypageJobFairService.retrieveApplicantListExcelByJobNoticeSeq(paramObj)
      .then( (res) => {
        loadingStore.loading = false;
        try {
          const href = URL.createObjectURL(res.data);

          // create "a" HTML element with href to file & click
          const link = document.createElement('a');
          link.href = href;
          link.setAttribute('download', `${moment().format("YYYYMMDD")}_입사지원리스트.xlsx`); //or any other extension
          document.body.appendChild(link);
          link.click();

          // clean up "a" element & remove ObjectURL
          document.body.removeChild(link);
          URL.revokeObjectURL(href);
        } catch(e) {
          console.error('다운로드 파일 처리 중 오류가 발생했습니다:', e);
        }
      }).catch( (err) =>  {
      loadingStore.loading = false;
      let errorMessage = '다운로드 데이터 생성 중 오류가 발생했습니다.';
      // Blob 응답 처리
      err.response.data.text()  // Blob을 텍스트로 변환
        .then(text => {
          try {
            // 텍스트를 JSON으로 파싱
            const responseData = JSON.parse(text);
            // 메시지 추출
            if (responseData?.result?.message) {
              errorMessage = responseData.result.message;
            }
          } catch (parseError) {
            console.error('에러 메시지 파싱 중 오류가 발생했습니다.:', parseError);
          }
          // Toast에 에러 메시지 표시
          $toast('error', errorMessage);
        });
    });
  }

  // 이력서 보기
  const onClickMyResume = (resumeId) => {
    jobFairResumeModal.value.show({
      resumeId,
      hideFooterBtn: true,
    })
  }
  
  // 입사지원 상태변경 joinStateGb
  const onClickApplyStateGb = async (resumeId, jobNoticeSeq, stateGb) => {
    let modalContent, okButtonText;
    if (stateGb === "02") {
      modalContent = ["지원취소 처리하시겠습니까?", "'지원취소'으로 상태가 변경됩니다."];
      okButtonText = "지원취소";
    } else if (stateGb === "03") {
      modalContent = ["서류합격 처리하시겠습니까?", "'서류합격'으로 상태가 변경됩니다."];
      okButtonText = "서류합격";
    } else if (stateGb === "04") {
      modalContent = ["서류불합격 처리하시겠습니까?", "'서류불합격'으로 상태가 변경됩니다."];
      okButtonText = "서류불합격";
    } else if (stateGb === "05") {
      modalContent = ["최종합격 처리하시겠습니까?", "'최종합격'으로 상태가 변경됩니다."];
      okButtonText = "최종합격";
    } else if (stateGb === "06") {
      modalContent = ["최종불합격 처리하시겠습니까?", "'최종불합격'으로 상태가 변경됩니다."];
      okButtonText = "최종불합격";
    }

    const ok = await confirm(modalContent, okButtonText);
    if(ok) {
      const paramObj = {
        resumeId,
        jobNoticeSeq,
        applyStateGb: stateGb,
      };

      mypageJobFairApplicantService.updateApplyStateGb(paramObj)
        .then( (res) => {
          loadingStore.loading = false;
          if(res.data.code.toLowerCase() === 'success') {
            alert("변경하였습니다.");
            retrieveList();
          } else {
            alert("변경을 실패하였습니다.");
          }
        })
        .catch( (res) =>  {
          loadingStore.loading = false;
          alert("변경을 실패하였습니다.");
        });
    }
  }

  onMounted(() => {
    retrieveList();
  });

  const emit = defineEmits(['go-back']);
  const goBack = () => {
    emit('go-back');
  };
</script>

<template>
  <div class="list-top">
    <KSASecondaryButton text="이전" @click="goBack()" />
  </div>
  <!-- 검색 자리 -->
  <div class="list-top">
      <p>총 <span>{{ $number(totalCount) }}</span>명</p>
       <div class="flex-wrap">
        <KSAPrimaryButton v-show="fileYn === 'N'"  text="이력서 다운로드" @click="onClickDownload()" />&nbsp;
        <KSAPrimaryButton text="채용공고 보기" @click="onClickJobNotice()" />&nbsp;
        <KSASelect :options="pageSizeGroup" class="select" @selectOption="onSelectPageSize"/>
      </div>
  </div>
  <div class="table-form board-mobile-vertical">
      <table>
          <tr>
              <th>NO</th>
              <th>이름</th>
              <th>이력서 제목</th>
              <th v-if="list[0]?.fileYn === 'N'">관심 카테고리</th>
              <th v-if="list[0]?.fileYn === 'N'">전공계열(주전공)</th>
              <th>입사 지원일</th>
              <th>상태</th>
              <th>상태변경</th>
          </tr>
          <tr v-for="applicant in list" :key="applicant.seq">
            <td>{{ applicant.boardNo }}</td>
            <td>{{ applicant.userName }}</td>
            <td class="text-overflow long-overflow">
              <span class="blue file" v-if="applicant.fileYn === 'Y'" v-for="resumeAttaches in applicant.resumeAttaches" @click="$downloadForPrivate({fileName:resumeAttaches.fileName, fileDir: resumeAttaches.fileDir })" style="cursor: pointer;">
                {{ resumeAttaches.fileName }}
              </span>
              <span class="clickable blue" v-else @click="onClickMyResume(applicant.resumeId)">
                {{ applicant.title }}
              </span>
            </td>
            <td v-if="applicant.fileYn === 'N'">{{ applicant.interests }}</td>
            <td v-if="applicant.fileYn === 'N'">{{ $gbNameKR('MAJOR_GB',applicant.majorGb) }}</td>
            <td>{{ $yyyymmddHHmmss(applicant.createDate) }}</td>
            <td>
              <span v-if="applicant.applyStateGb === '01'">{{ $gbNameKR('APPLY_STATE_GB',applicant.applyStateGb) }}</span>
              <span v-else-if="applicant.applyStateGb === '03' || applicant.applyStateGb === '05'" class="blue">{{ $gbNameKR('APPLY_STATE_GB',applicant.applyStateGb) }}</span>
              <span v-else-if="applicant.applyStateGb === '04' || applicant.applyStateGb === '06'" class="red">{{ $gbNameKR('APPLY_STATE_GB',applicant.applyStateGb) }}</span>
              <span v-else class="gray">{{ $gbNameKR('APPLY_STATE_GB',applicant.applyStateGb) }}</span>
            </td>
            <td>
              <div v-if="applicant.applyStateGb === '01'">
                <KSATextButton class="clickable black" text="서류합격" @click="onClickApplyStateGb(applicant.resumeId, applicant.jobNoticeSeq, '03')"/>
                &nbsp;
                <KSATextButton class="clickable black" text="서류불합격" @click="onClickApplyStateGb(applicant.resumeId, applicant.jobNoticeSeq, '04')"/>
              </div>
              <div v-else-if="applicant.applyStateGb === '03'">
                <KSATextButton class="clickable black" text="최종합격" @click="onClickApplyStateGb(applicant.resumeId, applicant.jobNoticeSeq, '05')"/>
                &nbsp;
                <KSATextButton class="clickable black" text="최종불합격" @click="onClickApplyStateGb(applicant.resumeId, applicant.jobNoticeSeq, '06')"/>
              </div>
              <div v-else>
                  -
              </div>
            </td>
          </tr>
      </table>
      <div class="result-none" v-if="list.length === 0">
          입사지원 내역이 없습니다.
      </div>
  </div>
  <Paginate
      v-if="list.length > 0"
      v-model="currentPage"
      :page-count="totalPages"
      :click-handler="onSelectedPage"
  />
  <JobFairResumeDetailModal ref="jobFairResumeModal"/> <!-- 특별채용관 이력서 상세 -->
  <JobFairJobNoticeDetailModal ref="jobNoticeModal" /> <!-- 채용공고 보기 -->
</template>

<style lang="scss" scoped>
@import "../../../../../../assets/scss/theme.scss";

/* Filter */
.filter-wrap {
    margin-bottom: 40px;
    &.open {
        p {
            &:before {
                transition-duration: 0.2s;
                -webkit-transform: translateY(-50%) rotate(225deg);
                transform: translateY(-50%) rotate(225deg);
            }
        }
        .filter {
            display: block;
            border-bottom: $border_line;
        }
    }
    p {
        position: relative;
        @include font-size_md;
        background: $gray-light_color;
        border: $border_line;
        font-weight: 700;
        cursor: pointer;
        padding: 10px 20px;
        &:before {
            content: "";
            position: absolute;
            top: 50%;
            right: 20px;
            display: block;
            width: 8px;
            height: 8px;
            border-top: 1px solid #000000;
            border-left: 1px solid #000000;
            -webkit-transform: translateY(-50%) rotate(45deg);
            transform: translateY(-50%) rotate(45deg);
            transition-duration: 0.2s;
        }
    }
    .filter {
        display: none;
        border-left: $border_line;
        border-right: $border_line;
        dl {
            display: flex;
            align-items: center;
            dt {
                display: flex;
                align-items: center;
                width: 180px;
                height: 48px;
                font-weight: 700;
                padding: 10px 10px 10px 20px;
                .select {
                    font-weight: initial;
                }
            }
            dd {
                width: calc(100% - 180px);
                padding: 10px 20px 10px 0;
                .grid {
                    span {
                        margin: 0 4px;
                    }
                }
            }
            &:first-of-type {
                padding-top: 10px;
            }
            &:last-of-type {
                padding-bottom: 10px;
            }
        }
        .btn-wrap {
            border-top: $border_line;
            padding: 10px 20px;
            margin: 0;
            button {
                margin: 0 5px;
            }
        }
        .text-box {
            margin-top: 0;
        }
    }
}

.clickable {
  cursor: pointer;
  &.cancel {
    color: #E71414;
    text-decoration: underline;
    &:hover {
      font-weight: 700;
    }
  }


}

.white-background {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: white;
}

.blueTextButton { color: $blue_color; }
</style>